/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Button, Flex, IconLoading, IconLongArrow, IconLongArrowLeft, Text } from "@powerledger/ui-component-lib";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { notifications } from "@/app/container/notifications";

import { useOnboardingContext } from "../onboarding/context";
import { StepButtonProps } from "./step-button.types";

export const StepButton: FC<StepButtonProps> = ({ loading, back, text, ...props }) => {
  const { t } = useTranslation();
  const { isSavingBeforeExit, nextSubmitting, setNextSubmitting, backSubmitting, setBackSubmitting } =
    useOnboardingContext();

  return (
    <Flex sx={{ display: "inline-flex" }}>
      <Button
        variant="primary"
        {...props}
        disabled={props.disabled || isSavingBeforeExit || nextSubmitting || backSubmitting}
        onClick={async (args) => {
          try {
            if (back) {
              setBackSubmitting(true);
            } else setNextSubmitting(true);
            if (props.onClick) await props.onClick(args);
          } catch (e: unknown) {
            if (e instanceof Error) {
              notifications.error({
                description: t(e?.message),
              });
            }
          } finally {
            if (back) {
              setBackSubmitting(false);
            } else setNextSubmitting(false);
          }
        }}
      >
        {back ? (
          <>
            <IconLongArrowLeft size={4} noSvgPositioning sx={{ mr: 2 }} stroke={"white"} />
            <Text data-testid={"step-button--back"}>{t(text || "Back")}</Text>
          </>
        ) : (
          <>
            <Text data-testid={"step-button--next"}>{t(text || "Next")}</Text>
            <IconLongArrow size={4} noSvgPositioning sx={{ ml: 2 }} stroke={"white"} />
          </>
        )}
      </Button>
      {!isSavingBeforeExit && (back ? backSubmitting : nextSubmitting) && <IconLoading sx={{ ml: 2 }} size="small" />}
    </Flex>
  );
};
